import type { DeviceCapabilities, DeviceListItem } from '../api/devices';

/// Pure transforms over `DeviceListItem` — extracted out of the
/// component so they can be unit-tested without touching React.

/// Stable ordering for the Settings list: the current device pins to
/// the top, then newest-first by `created_at` so a freshly paired
/// device is easy to find.
export function sortDevices(items: DeviceListItem[]): DeviceListItem[] {
  return [...items].sort((a, b) => {
    const aCurrent = a.current === true;
    const bCurrent = b.current === true;
    if (aCurrent !== bCurrent) return aCurrent ? -1 : 1;
    return b.created_at.localeCompare(a.created_at);
  });
}

/// Map the server's free-text `device_type` to a friendly label. Falls
/// through to title-casing the raw string so new platforms surface
/// readably even before this mapping ships.
export function platformLabel(deviceType: string): string {
  switch (deviceType.toLowerCase()) {
    case 'desktop':
    case 'macos':
    case 'mac':
      return 'Mac';
    case 'mobile':
    case 'ios':
    case 'iphone':
      return 'iPhone';
    case 'tablet':
    case 'ipad':
      return 'iPad';
    case 'android':
      return 'Android';
    case 'windows':
      return 'Windows';
    case 'linux':
      return 'Linux';
    case 'web':
    case 'browser':
      return 'Web';
    default:
      if (!deviceType) return 'Unknown';
      return deviceType.charAt(0).toUpperCase() + deviceType.slice(1);
  }
}

/// Locale-aware relative-time formatter, capped at a few common
/// buckets. Returns `"just now"` for sub-minute gaps and falls back to
/// a localized absolute date past a week.
const relative = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });

export function relativeTime(iso: string, now: Date = new Date()): string {
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return iso;
  const diffMs = then.getTime() - now.getTime();
  const diffSec = Math.round(diffMs / 1000);
  const absSec = Math.abs(diffSec);
  if (absSec < 60) return 'just now';
  const diffMin = Math.round(diffSec / 60);
  if (Math.abs(diffMin) < 60) return relative.format(diffMin, 'minute');
  const diffHour = Math.round(diffMin / 60);
  if (Math.abs(diffHour) < 24) return relative.format(diffHour, 'hour');
  const diffDay = Math.round(diffHour / 24);
  if (Math.abs(diffDay) < 7) return relative.format(diffDay, 'day');
  return then.toLocaleDateString();
}

/// Turn a chip slug into a display label, mirroring the Mac client:
/// `"m2_pro"` → `"Apple M2 Pro"`, `"m1"` → `"Apple M1"`, `"intel"` →
/// `"Intel"`.
function chipDisplayName(chip: string): string {
  if (!chip || chip === 'intel') return 'Intel';
  const pretty = chip
    .split('_')
    .map((part) =>
      // "m2" stays fully uppercased; "pro"/"max"/"ultra" title-case.
      /^m\d+$/.test(part)
        ? part.toUpperCase()
        : part.charAt(0).toUpperCase() + part.slice(1),
    )
    .join(' ');
  return `Apple ${pretty}`;
}

/// One-line hardware summary for the device row, e.g.
/// `"Apple M2 Pro · 16 GB · Neural Engine"`. Returns `null` when the
/// blob is absent or carries nothing renderable, so the caller can omit
/// the line entirely. Kept consistent with the Mac client's
/// `DeviceCapabilities.displaySummary`.
export function capabilitiesSummary(
  capabilities: DeviceCapabilities | undefined,
): string | null {
  if (!capabilities) return null;
  const parts: string[] = [];
  if (capabilities.chip) {
    parts.push(chipDisplayName(capabilities.chip));
  }
  if (typeof capabilities.total_memory_gb === 'number' && capabilities.total_memory_gb > 0) {
    parts.push(`${capabilities.total_memory_gb} GB`);
  }
  if (capabilities.neural_engine) {
    parts.push('Neural Engine');
  }
  return parts.length > 0 ? parts.join(' · ') : null;
}
